import React, { useState, useEffect, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { wsService } from '../services/WebSocketService';
import { DataTable } from './DataTable';
import './ATROptimizationModal.css';

interface ATROptimizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyResults: (mappings: ATRMapping[]) => void;
}

interface ATRMapping {
  atrMin: number;
  atrMax: number;
  volumeMultiplier: number;
  minVolumeRatio: number;
  label: string;
}

interface BucketResult {
  atrMin: number;
  atrMax: number;
  label: string;
  candleCount: number;
  tradeCount: number;
  winRate: number;
  profitFactor: number;
  expectancy: number;
  bestVolumeMultiplier: number;
  bestMinVolumeRatio: number;
}

interface OptimizationProgress {
  stage: string;
  progress: number;
  currentBucket?: string;
  message?: string;
}

interface OptimizationResult {
  success: boolean;
  bucketResults: BucketResult[];
  totalCandles: number;
  totalTrades: number;
  overallWinRate: number;
  overallProfitFactor: number;
  recommendedMappings: ATRMapping[];
  error?: string;
}

// Bucket results table column definitions
const bucketResultColumns: ColumnDef<BucketResult, unknown>[] = [
  {
    accessorKey: 'label',
    header: 'ATR Range',
    cell: ({ row }) => {
      const bucket = row.original;
      return (
        <div className="bucket-label">
          <span className={`atr-badge atr-${bucket.label.toLowerCase().replace(' ', '-')}`}>
            {bucket.label}
          </span>
          <span className="atr-range-small">
            {bucket.atrMin}-{bucket.atrMax === 999 ? '+' : bucket.atrMax}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: 'candleCount',
    header: 'Candles',
    cell: info => (info.getValue() as number).toLocaleString(),
  },
  {
    accessorKey: 'tradeCount',
    header: 'Trades',
    cell: info => String(info.getValue()),
  },
  {
    accessorKey: 'winRate',
    header: 'Win %',
    cell: info => {
      const winRate = info.getValue() as number;
      return (
        <span className={winRate >= 0.5 ? 'green' : 'red'}>
          {(winRate * 100).toFixed(0)}%
        </span>
      );
    },
  },
  {
    accessorKey: 'profitFactor',
    header: 'PF',
    cell: info => {
      const pf = info.getValue() as number;
      const className = pf >= 1.5 ? 'green' : pf >= 1 ? 'yellow' : 'red';
      return <span className={className}>{pf.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'bestVolumeMultiplier',
    header: 'Mult',
    cell: info => <span className="recommended">{(info.getValue() as number).toFixed(1)}x</span>,
  },
  {
    accessorKey: 'bestMinVolumeRatio',
    header: 'Ratio',
    cell: info => <span className="recommended">{info.getValue() as number}x</span>,
  },
];

const ATROptimizationModal: React.FC<ATROptimizationModalProps> = ({
  isOpen,
  onClose,
  onApplyResults,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<OptimizationProgress | null>(null);
  const [result, setResult] = useState<OptimizationResult | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<'profitFactor' | 'winRate' | 'expectancy'>('profitFactor');
  const [daysToAnalyze, setDaysToAnalyze] = useState(30);

  // Socket event handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleProgress = (data: OptimizationProgress) => {
      setProgress(data);
    };

    const handleResult = (data: OptimizationResult) => {
      setResult(data);
      setIsRunning(false);
      setProgress(null);
    };

    wsService.on('atr-optimization-progress', handleProgress);
    wsService.on('atr-optimization-result', handleResult);

    return () => {
      wsService.off('atr-optimization-progress', handleProgress);
      wsService.off('atr-optimization-result', handleResult);
    };
  }, [isOpen]);

  const handleStartOptimization = useCallback(() => {
    setIsRunning(true);
    setResult(null);
    setProgress({ stage: 'Starting...', progress: 0 });

    wsService.emit('start-atr-optimization', {
      days: daysToAnalyze,
      metric: selectedMetric,
    });
  }, [daysToAnalyze, selectedMetric]);

  const handleApply = useCallback(() => {
    if (result?.recommendedMappings) {
      onApplyResults(result.recommendedMappings);
      onClose();
    }
  }, [result, onApplyResults, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isRunning) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="optimization-modal-backdrop" onClick={handleBackdropClick}>
      <div className="optimization-modal">
        {/* Header */}
        <div className="optimization-modal-header">
          <h2>ATR Threshold Optimization</h2>
          <button
            className="optimization-close-btn"
            onClick={onClose}
            disabled={isRunning}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="optimization-modal-content">
          {/* Configuration */}
          {!isRunning && !result && (
            <div className="optimization-config">
              <p className="optimization-description">
                Analyze historical data to find optimal volume thresholds for each ATR range.
                The optimizer will test different combinations and recommend the best settings
                based on your chosen metric.
              </p>

              <div className="optimization-setting">
                <label>Days to Analyze</label>
                <div className="optimization-input-row">
                  <input
                    type="range"
                    min="7"
                    max="90"
                    value={daysToAnalyze}
                    onChange={(e) => setDaysToAnalyze(Number(e.target.value))}
                    className="optimization-slider"
                  />
                  <span className="optimization-value">{daysToAnalyze} days</span>
                </div>
              </div>

              <div className="optimization-setting">
                <label>Optimization Metric</label>
                <div className="optimization-metric-buttons">
                  <button
                    className={`metric-btn ${selectedMetric === 'profitFactor' ? 'active' : ''}`}
                    onClick={() => setSelectedMetric('profitFactor')}
                  >
                    Profit Factor
                  </button>
                  <button
                    className={`metric-btn ${selectedMetric === 'winRate' ? 'active' : ''}`}
                    onClick={() => setSelectedMetric('winRate')}
                  >
                    Win Rate
                  </button>
                  <button
                    className={`metric-btn ${selectedMetric === 'expectancy' ? 'active' : ''}`}
                    onClick={() => setSelectedMetric('expectancy')}
                  >
                    Expectancy
                  </button>
                </div>
              </div>

              <button
                className="optimization-start-btn"
                onClick={handleStartOptimization}
              >
                Start Optimization
              </button>
            </div>
          )}

          {/* Progress */}
          {isRunning && progress && (
            <div className="optimization-progress">
              <div className="progress-spinner" />
              <div className="progress-info">
                <div className="progress-stage">{progress.stage}</div>
                {progress.currentBucket && (
                  <div className="progress-bucket">Analyzing: {progress.currentBucket}</div>
                )}
                {progress.message && (
                  <div className="progress-message">{progress.message}</div>
                )}
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              <div className="progress-percentage">{progress.progress.toFixed(0)}%</div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="optimization-results">
              {result.success ? (
                <>
                  {/* Summary */}
                  <div className="results-summary">
                    <div className="summary-stat">
                      <span className="stat-label">Candles Analyzed</span>
                      <span className="stat-value">{result.totalCandles.toLocaleString()}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Total Trades</span>
                      <span className="stat-value">{result.totalTrades}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Overall Win Rate</span>
                      <span className="stat-value green">
                        {(result.overallWinRate * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Profit Factor</span>
                      <span className={`stat-value ${result.overallProfitFactor >= 1.5 ? 'green' : result.overallProfitFactor >= 1 ? 'yellow' : 'red'}`}>
                        {result.overallProfitFactor.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Bucket Results Table */}
                  <div className="results-table-container">
                    <DataTable
                      data={result.bucketResults}
                      columns={bucketResultColumns}
                      className="results-table"
                      sortable={false}
                      compact
                    />
                  </div>

                  {/* Actions */}
                  <div className="results-actions">
                    <button
                      className="optimization-btn secondary"
                      onClick={() => {
                        setResult(null);
                        setProgress(null);
                      }}
                    >
                      Run Again
                    </button>
                    <button
                      className="optimization-btn primary"
                      onClick={handleApply}
                    >
                      Apply Recommendations
                    </button>
                  </div>
                </>
              ) : (
                <div className="optimization-error">
                  <div className="error-icon">!</div>
                  <div className="error-message">{result.error || 'Optimization failed'}</div>
                  <button
                    className="optimization-btn secondary"
                    onClick={() => {
                      setResult(null);
                      setProgress(null);
                    }}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ATROptimizationModal;
